import { Divider, Stack } from '@mui/material';
import { useCallback, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LassieParams, LassieWorkstation, Templates } from '@thive/fddm-types';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { WorkspaceMode } from '@/modules/common/types/general';
import {
  getMultiSelectDisplayValue,
  MultiSelectInput,
  NumberInput,
} from '@/modules/common/components/inputs';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { TpsServiceFactory } from '../service/TpsServiceFactory';
import { WorkstationConfigs } from './workstation';
import { DisabledReasonTooltip } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/DisabledReasonTooltip';
import { UserPreferenceName } from '@/modules/userPreferences';
import { getRegeneratedTemplateId } from '../helpers';
import {
  FIELDS_PADDING_BOTTOM,
  FIELDS_SPACING,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

interface Props {
  selectedLassies: Templates['lassies'];
}

function LassieTemplateParametersComponent({ selectedLassies }: Props) {
  const { t } = useTranslation();
  const mode = useAppStore((state) => state.mode);
  const loadingSvc = useMemo(() => LoadingServiceFactory.getInstance().createLoadingService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const tpsSvc = useMemo(() => TpsServiceFactory.getInstance().createTpsService(), []);

  const lassieGroup = selectedLassies[0];
  const newGenerationParams = lassieGroup?.params;
  const onlyOneSelected = selectedLassies.length === 1;

  const sprinklerOptions = useMemo(() => {
    if (!newGenerationParams) return [];

    return Array.from(Array(newGenerationParams.numShelves - 1), (el, i) => i + 1).map((val) => ({
      label: t('interface:properties.template.sprinklers.option_label', {
        value: val,
      }),
      value: String(val),
    }));
  }, [newGenerationParams, t]);

  const sprinklersDisplayValue = useMemo(() => {
    if (!newGenerationParams) return '';

    return getMultiSelectDisplayValue(
      newGenerationParams.sprinklerShelves.map((val) => ({
        name: String(val),
        id: String(val),
      })),
      newGenerationParams.sprinklerShelves.map((item) => String(item)),
    );
  }, [newGenerationParams]);

  const regenerate = useCallback(
    async (params?: Partial<LassieParams>) => {
      if (!newGenerationParams || !lassieGroup) return;

      loadingSvc.showLoadContainer('TRANSPARENT');

      const regeneration = await tpsSvc.regenerateLassieTemplate({
        ...lassieGroup,
        params: { ...newGenerationParams, ...params },
      });
      // Regenerating can give the template a new id: keep it selected
      tpsSvc.selectTemplates([getRegeneratedTemplateId(regeneration) ?? lassieGroup.group.id]);

      loadingSvc.hideLoadContainer();
    },
    [newGenerationParams, lassieGroup, tpsSvc, loadingSvc],
  );

  const onNumAislesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassie.numAisles', newValue);
      regenerate({ numAisles: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumBaysChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassie.numBays', newValue);
      regenerate({ numBays: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumShelvesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassie.numShelves', newValue);
      regenerate({ numShelves: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onSprinklerOptionClick = useCallback(
    (value: string) => {
      if (!newGenerationParams) return;

      const newValue = Number(value);
      const { numShelves, sprinklerShelves } = newGenerationParams;
      let updated = [...sprinklerShelves];
      updated = updated.filter((sprinklerLevel) => sprinklerLevel <= numShelves);
      updated = updated.includes(newValue)
        ? updated.filter((item) => item !== newValue)
        : [...updated, newValue];
      updated.sort((a, b) => a - b);

      regenerate({ sprinklerShelves: updated });
    },
    [newGenerationParams, regenerate],
  );

  const onConfigUpdate = useCallback(
    (newValue: LassieWorkstation[]) => {
      regenerate({ workstations: newValue });
    },
    [regenerate],
  );

  const onAddWorkstationConfigClick = useCallback(() => {
    if (!newGenerationParams) return;

    const { workstations } = newGenerationParams;
    const newWorkstations: LassieWorkstation = {
      type: 'SINGLE_RAMP_TYPE_A',
      positionFromBottom: 0,
    };

    regenerate({ workstations: [...workstations, newWorkstations] });
  }, [newGenerationParams, regenerate]);

  const isDisabled = mode !== WorkspaceMode.EDITABLE || !onlyOneSelected;

  if (!newGenerationParams) return null;

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_LASSIE}
      title={t('interface:properties.template_title.lassie', 'Lassie')}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
        <PropertiesLabel i18nkey='interface:properties.template.aisles'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <NumberInput
              value={newGenerationParams.numAisles}
              onChange={onNumAislesChange}
              disabled={isDisabled}
              minValue={1}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.bays'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <NumberInput
              value={newGenerationParams.numBays}
              onChange={onNumBaysChange}
              disabled={isDisabled}
              minValue={1}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.shelves'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <NumberInput
              value={newGenerationParams.numShelves}
              onChange={onNumShelvesChange}
              minValue={1}
              disabled={isDisabled}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.sprinklers.input_label'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <MultiSelectInput
              options={sprinklerOptions}
              selectedOptions={newGenerationParams.sprinklerShelves.map((item) => String(item))}
              onItemClick={onSprinklerOptionClick}
              displayValue={sprinklersDisplayValue}
              disabled={isDisabled}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      </Stack>
      <Divider />
      <WorkstationConfigs
        configs={newGenerationParams.workstations}
        onAddClick={onAddWorkstationConfigClick}
        updateConfigs={onConfigUpdate}
      />
    </Accordion>
  );
}

export const LassieTemplateParameters = memo(LassieTemplateParametersComponent);
